import { useEffect, useState } from "react";
import axios from "axios";
import BlogCard from "@/components/BlogCard";
import SEO, { SITE_URL } from "@/components/SEO";
import { API } from "@/lib/apiBase";

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <div className="page-wrapper bg-ink" data-testid="blog-page">
      <SEO
        title="Blog — Planning Insights & Resource Consent Articles"
        description="Planning insights, regulatory updates and practical advice for property owners, developers and architects in Auckland and across New Zealand."
        path="/blog"
        keywords="resource consent blog, planning advice nz, cross lease conversion guide, auckland planning insights"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Code Planning — Blog",
          url: `${SITE_URL}/blog`,
          publisher: { "@id": `${SITE_URL}/#organization` },
          blogPost: posts.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${SITE_URL}/blog/${p.slug}`,
            datePublished: p.created_at,
            author: { "@type": "Organization", name: p.author },
          })),
        }}
      />
      {/* Header */}
      <section className="py-12 md:py-16 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
            Insights & Updates
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Blog
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed">
            Planning insights, regulatory updates, and practical advice
            for property owners and developers in New Zealand.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-neutral-800 animate-pulse h-[400px]" />
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-400">No blog posts available yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-10 md:py-14 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
            Stay Informed
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Get planning updates and insights delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Your email address"
              className="input-underline flex-1 text-center sm:text-left"
              data-testid="newsletter-email"
            />
            <button className="btn-primary" data-testid="newsletter-submit">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
